import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

/**
 * Sage cube engine API client.
 *
 * Mirrors the controller in `apps/api/src/cubes/cubes.controller.ts`:
 *   GET    /cubes
 *   GET    /cubes/:name
 *   GET    /cubes/:name/builds
 *   POST   /cubes/:name/builds
 *   POST   /cubes/:name/query
 */

export type CubeTier = 'platform' | 'customer';
export type CubeBuildMode = 'incremental' | 'backfill';
export type CubeBuildStatus = 'running' | 'succeeded' | 'failed' | 'cancelled';

export interface CubeSummary {
  name: string;
  title: string;
  tier: CubeTier;
  description: string | null;
  sourceKind: 'sisense' | 'dataset' | 'inline';
  dimensions: string[];
  measures: string[];
  preAggregations: string[];
  refreshCron: string;
  retention: string | null;
  lastSucceededAt: string | null;
  lastSucceededWatermark: string | null;
  lastBuildStatus: CubeBuildStatus | null;
  /** CSAGE-502: newest date the cube holds ('YYYY-MM-DD' or
   *  'YYYY-MM-DD HH:MM:SS', UTC). Null when unknown or no time dimension. */
  newestDataAt: string | null;
  /** Freshness limit in days; null when the cube is never checked. */
  staleAfterDays: number | null;
  /** Informational: newest data is older than the limit. It does not
   *  change lastBuildStatus, and a stale cube still serves queries. */
  dataStale: boolean;
}

export interface CubeBuildHistoryRow {
  id: string;
  cubeName: string;
  mode: CubeBuildMode;
  status: CubeBuildStatus;
  rowsIngested: number;
  durationMs: number | null;
  watermarkFrom: string | null;
  watermarkTo: string | null;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
  triggeredBy: string;
  newestDataAt: string | null;
  dataStale: boolean | null;
}

export interface CubeBuildResult {
  buildId: string;
  cubeName: string;
  mode: CubeBuildMode;
  status: CubeBuildStatus;
  rowsIngested: number;
  durationMs: number;
  watermarkFrom: string | null;
  watermarkTo: string | null;
  clickhouseTable: string;
  preAggregationsRebuilt: string[];
  newestDataAt: string | null;
  dataStale: boolean;
}

export function useCubes() {
  const api = useApi();
  return useQuery<CubeSummary[]>({
    queryKey: ['cubes'],
    queryFn: () => api.fetch('/cubes'),
    refetchInterval: 30_000, // refresh every 30s — cube state is somewhat live
  });
}

export function useCube(name: string | null) {
  const api = useApi();
  return useQuery<CubeSummary>({
    queryKey: ['cubes', name],
    queryFn: () => api.fetch(`/cubes/${encodeURIComponent(name as string)}`),
    enabled: !!name,
    refetchInterval: 30_000,
  });
}

export function useCubeBuilds(name: string | null, limit = 25) {
  const api = useApi();
  return useQuery<CubeBuildHistoryRow[]>({
    queryKey: ['cubes', name, 'builds', limit],
    queryFn: () => api.fetch(`/cubes/${encodeURIComponent(name as string)}/builds?limit=${limit}`),
    enabled: !!name,
    refetchInterval: 10_000, // tighter polling so a running build feels live
  });
}

export function useTriggerCubeBuild(name: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<CubeBuildResult, Error, { mode: CubeBuildMode }>({
    mutationFn: (body) =>
      api.fetch(`/cubes/${encodeURIComponent(name)}/builds`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cubes', name] });
      void qc.invalidateQueries({ queryKey: ['cubes', name, 'builds'] });
      void qc.invalidateQueries({ queryKey: ['cubes'] });
    },
  });
}

// ─── Data status (CSAGE-543) ──────────────────────────────────────────────

export type DataAreaFreshness = 'fresh' | 'stale' | 'snapshot' | 'no_data' | 'unavailable';

export interface DataAreaStatus {
  area: string;
  label: string;
  /** Latest data date within the caller's scope; null for snapshot/no-data. */
  latest: string | null;
  staleAfterDays: number | null;
  status: DataAreaFreshness;
}

/** Per-data-area freshness, scope-aware. Internal-Canopy-admin only
 *  (server-gated); the numbers reflect the admin's position in the
 *  hierarchy. */
export function useDataStatus() {
  const api = useApi();
  return useQuery<DataAreaStatus[]>({
    queryKey: ['cubes', 'data-status'],
    queryFn: () => api.fetch('/cubes/data-status'),
    staleTime: 60_000,
  });
}

// Tier 2 diagnosis + Tier 3 feed lifecycle (CSAGE-543).
export type CubeDiagnosis =
  | 'ok'
  | 'source_stalled'
  | 'build_failing'
  | 'never_built'
  | 'not_checked';
export type FeedLifecycle = 'active' | 'migrating' | 'decommissioned' | 'legacy';

export interface FeedAnnotation {
  feed: string;
  lifecycle: FeedLifecycle;
  note: string;
  ticket: string | null;
}

export interface CubeHealthRow extends CubeSummary {
  diagnosis: CubeDiagnosis;
  diagnosisReason: string;
  feed: FeedAnnotation | null;
}

/** Internal-Canopy-admin-only cube/pipeline health (all cubes), enriched with
 *  a diagnosis + feed lifecycle. Pass the admin flag as `enabled` so non-admins
 *  never 403-poll. */
export function useCubeHealth(enabled = true) {
  const api = useApi();
  return useQuery<CubeHealthRow[]>({
    queryKey: ['cubes', 'health'],
    queryFn: () => api.fetch('/cubes/health'),
    enabled,
    staleTime: 30_000,
  });
}

// ─── Backfill queue (CSAGE-433) ───────────────────────────────────────────

export type BackfillStatus =
  | 'pending'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface CubeBackfill {
  id: string;
  cubeName: string;
  since: string;
  until: string;
  chunkUnit: 'month' | 'week' | 'day';
  cursor: string;
  chunksTotal: number;
  chunksDone: number;
  status: BackfillStatus;
  lastError: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface BackfillQueue {
  window: string;
  inWindow: boolean;
  lastDrainAt: string | null;
  lastDrainOutcome: string | null;
  backfills: CubeBackfill[];
}

export function useCubeBackfills() {
  const api = useApi();
  return useQuery<BackfillQueue>({
    queryKey: ['cubes', 'backfills'],
    queryFn: () => api.fetch('/cubes/backfills'),
    refetchInterval: 15_000,
  });
}

export function useCreateCubeBackfill() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    CubeBackfill,
    Error,
    { cubeName: string; since: string; until: string; chunkUnit?: 'month' | 'week' | 'day' }
  >({
    mutationFn: ({ cubeName, ...body }) =>
      api.fetch(`/cubes/${encodeURIComponent(cubeName)}/backfills`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cubes', 'backfills'] }),
  });
}

export function useBackfillAction() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<CubeBackfill, Error, { id: string; action: 'pause' | 'resume' | 'cancel' }>({
    mutationFn: ({ id, action }) =>
      api.fetch(`/cubes/backfills/${encodeURIComponent(id)}/${action}`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cubes', 'backfills'] }),
  });
}
